import React from 'react';
import { motion } from 'framer-motion';
import { FaCrown } from 'react-icons/fa';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatNumber } from './formatters';

const RANK_COLORS = [
  'bg-amber-100 text-amber-700',
  'bg-gray-100 text-gray-700',
  'bg-orange-100 text-orange-700',
];

const TopProductsCard = ({ products, isLoading, delay = 0 }) => {
  const { t } = useLanguage();
  const items = Array.isArray(products) ? products.slice(0, 5) : [];
  const maxSold = items.reduce((m, p) => Math.max(m, Number(p.totalSold) || 0), 0) || 1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col"
    >
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-50">
            <FaCrown className="h-4 w-4 text-amber-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {t('dashboard.topProducts.title')}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {t('dashboard.topProducts.subtitle')}
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 px-6 py-4">
        {isLoading ? (
          <div className="space-y-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse h-10 bg-gray-100 rounded" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-400">
            {t('dashboard.topProducts.empty')}
          </div>
        ) : (
          <ol className="space-y-3">
            {items.map((p, i) => {
              const sold = Number(p.totalSold) || 0;
              const pct = Math.max(4, Math.round((sold / maxSold) * 100));
              return (
                <li key={p.pharmacode || p.name || i} className="flex items-center gap-3">
                  <span
                    className={`flex-none w-6 h-6 rounded-md text-xs font-bold flex items-center justify-center ${
                      RANK_COLORS[i] || 'bg-indigo-50 text-indigo-700'
                    }`}
                  >
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {p.name || p.description || `#${p.pharmacode}`}
                    </div>
                    <div className="mt-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-indigo-500 to-purple-600"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 tabular-nums whitespace-nowrap">
                    {t('dashboard.topProducts.unitsSold', { count: formatNumber(sold) })}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </motion.div>
  );
};

export default TopProductsCard;
